// FCM push-token registration — the React port's half of what the Flutter app
// did on login. The backend already OWNS the sending side: it reads
// `Users.PushId` and pushes device events (Home Automation api/index.js,
// api/new/asynccontrollers.js, and api/new/notify.js — notify() is the one call
// for new events: text and/or image and/or a sound). The one thing
// missing from this app was WRITING that column, so a React-only user had an
// empty PushId and got no notifications.
//
// ⚠️ NATIVE ONLY. Push notifications do not exist in a browser or a Capacitor
// webview preview — `@capacitor/push-notifications` is an FCM/APNs bridge, so
// every entry point is gated on isNativePlatform() and no-ops on web/desktop.
// This cannot be tested at a desk; it needs an installed build on a real phone.
//
// ⚠️ PROJECT MUST MATCH THE SENDER. The token this mints belongs to the Firebase
// project in android/app/google-services.json (smartifyautomation-1a2c9), which
// is the SAME project the backend authenticates as. A token from any other
// project is accepted by FCM and then fails every send, silently — the exact
// trap SharpAccounting's pushServer.js documents. Do not swap the google-services.json
// for a different project without moving the backend sender with it.

import { updateUser } from '../api/api';
import { registerPushToken } from '../api/notifyApi';
import { isNativePlatform } from '../platform';
import { createSoundChannels } from './pushChannels';

// Where a tapped notification goes. Set by the mobile shell (it owns the
// router); a tap that arrives before it is set is held and replayed.
let openHandler = null;
let pendingOpen = null;
export function setPushOpenHandler(fn) {
  openHandler = fn;
  if (fn && pendingOpen) { const d = pendingOpen; pendingOpen = null; fn(d); }
}

// The account the token should be written to. It is the SIGNED-IN user, never a
// Super Admin's view-as id — the phone belongs to the real account, and a
// notification for "your device" must reach the person holding it. AppContext
// passes realUserId for exactly this reason.
let targetUserId = 0;

// The last token we successfully wrote, and for whom. FCM re-fires 'registration'
// on every launch with the same token; without this guard every cold start would
// POST an identical UpdateUsers. A refreshed token (rotation, reinstall) differs
// and does get written.
let lastWritten = { userId: 0, token: '' };

// Added exactly once. The plugin keeps delivering to whatever we registered, so
// re-adding on every login would stack duplicate handlers that all write.
let listenersReady = false;

// The token the device last handed us, held so a login that arrives AFTER the
// token can still write it (the 'registration' event can land before a userId
// exists — auto-login is async).
let pendingToken = '';

async function writeToken(token) {
  const uid = Number(targetUserId) || 0;
  if (!uid || !token) return;
  if (lastWritten.userId === uid && lastWritten.token === token) return;
  try {
    // RegisterPushToken also takes the token OFF any other account first, so a
    // phone that switched accounts stops getting the previous account's alerts.
    // It ships with the server; until then (HTML 404 -> false) fall back to the
    // generic UpdateUsers write, which is what the backend's senders read.
    let saved = false;
    try { saved = await registerPushToken(uid, token); } catch (e) { saved = false; }
    if (!saved) await updateUser({ PushId: token }, `UserId=${uid}`);
    lastWritten = { userId: uid, token };
  } catch (e) {
    // A failed write is not fatal and must not break login — the next launch
    // fires 'registration' again and retries. Never throw out of here.
    // eslint-disable-next-line no-console
    console.warn('[push] failed to save token:', e && e.message);
  }
}

async function ensureListeners(PushNotifications) {
  if (listenersReady) return;
  listenersReady = true;

  // The device's FCM token — fires after register() succeeds, and again on every
  // token rotation. This is the value that goes into Users.PushId.
  await PushNotifications.addListener('registration', (t) => {
    pendingToken = (t && t.value) || '';
    writeToken(pendingToken);
  });

  // A registration failure is reported, not retried in a loop — the usual cause
  // is a google-services.json / project mismatch or missing Play Services, which
  // a retry cannot fix.
  await PushNotifications.addListener('registrationError', (err) => {
    // eslint-disable-next-line no-console
    console.warn('[push] registration error:', err && (err.error || err));
  });

  // A tap on a notification (app backgrounded or killed). The payload's `data`
  // is what the server put there — notify.js always sends title/body/sound and
  // optionally type/route/uniqueNumber.
  await PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
    const data = (action && action.notification && action.notification.data) || {};
    // The Room Door Lock's Open / tap (DoorRequestMessagingService.java) comes
    // through here too; that notification is ours to clear once it is handled.
    const doorId = Number(data.doorNotificationId);
    if (doorId) {
      PushNotifications.removeDeliveredNotifications({ notifications: [{ id: doorId, data: {} }] })
        .catch(() => {});
    }
    if (openHandler) openHandler(data); else pendingOpen = data;
  });

  // Foreground delivery is drawn by the plugin itself — capacitor.config.json
  // PushNotifications.presentationOptions. Without that an alert that arrives
  // while the app is open is simply never shown.
}

/**
 * Where a tapped notification should land, from its data payload — or null to
 * just open the app. `route` (an in-app path) wins; a device event carrying
 * `uniqueNumber` opens that device.
 */
export function pushTargetPath(data) {
  const route = String((data && data.route) || '');
  if (route.startsWith('/') && !route.startsWith('//')) return route;
  const uno = String((data && (data.uniqueNumber || data.UniqueNumber)) || '').trim();
  // "Someone is at the door" -> Open: the lock screen, which asks for the
  // fingerprint at once and then sends LOCKUNLOCK. Nothing unlocks without it.
  // DOORBELL is the server's bell alert (Home Automation index.js); UNLOCK_REQUEST the older shape.
  const type = data && data.type;
  const doorbell = type === 'DOORBELL' || type === 'UNLOCK_REQUEST';
  if (/^\d+$/.test(uno) && doorbell && data.pushAction === 'open') {
    return `/device/${uno}?unlock=1`;
  }
  if (/^\d+$/.test(uno)) return `/device/${uno}`;
  return null;
}

/** Forget the last write, so the next login writes the token for its account. */
export function resetPushRegistration() {
  lastWritten = { userId: 0, token: '' };
  targetUserId = 0;
}

/**
 * Register this device for push and record its token against `userId`.
 * Safe to call on every login/foreground — it de-dupes writes and installs its
 * listeners only once. No-ops on web and when userId is falsy.
 */
export async function registerPush(userId) {
  targetUserId = Number(userId) || 0;
  if (!isNativePlatform() || !targetUserId) return;

  // If the token already arrived under a previous (or no) user, write it now for
  // this user without waiting for another 'registration' event.
  if (pendingToken) writeToken(pendingToken);

  let PushNotifications;
  try {
    // Imported lazily so the web bundle never pulls the native plugin in.
    ({ PushNotifications } = await import('@capacitor/push-notifications'));
  } catch (e) {
    return; // plugin absent (e.g. not synced) — nothing to do
  }

  try {
    await ensureListeners(PushNotifications);
    // Before register(): a push that arrives on first launch must find its
    // channel already there, or it plays the wrong sound forever after.
    await createSoundChannels(PushNotifications);

    let perm = await PushNotifications.checkPermissions();
    if (perm.receive === 'prompt' || perm.receive === 'prompt-with-rationale') {
      perm = await PushNotifications.requestPermissions();
    }
    if (perm.receive !== 'granted') return; // user declined — respect it

    // Triggers the 'registration' event above with the FCM token.
    await PushNotifications.register();
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('[push] register failed:', e && e.message);
  }
}

export default registerPush;
